import {
    memo, ReactNode, useEffect,
} from 'react';
import clsx from 'clsx';
import { InfoIcon } from 'src/modules/common/icons/InfoIcon.tsx';
import { CheckCircleIcon } from 'src/modules/common/icons/CheckCircleIcon.tsx';
import { WarningIcon } from 'src/modules/common/icons/WarningIcon.tsx';
import { ErrorIcon } from 'src/modules/common/icons/ErrorIcon.tsx';
import { CrossIcon } from 'src/modules/common/icons/CrossIcon.tsx';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

type Props = {
    type?: ToastType;
    isLowContrast?: boolean;
    title?: ReactNode;
    message: ReactNode;
    onClose?: () => void;
    duration?: number;
}

export const Toast = memo(({
    type = 'info', isLowContrast = false, title, message, onClose, duration,
}: Props) => {
    const icon = {
        info: <InfoIcon/>,
        success: <CheckCircleIcon/>,
        warning: <WarningIcon/>,
        error: <ErrorIcon/>,
    };

    useEffect(() => {
        if (!duration || !onClose) {
            return undefined;
        }

        const timer = setTimeout(onClose, duration);

        return () => clearTimeout(timer);
    }, [duration, onClose]);

    return (
        <div
            className={clsx({
                'vss-toast': true,
                [`vss-toast--${type}`]: true,
                [`vss-toast--${type}-low-contrast`]: isLowContrast,
            })}
            role="status"
            aria-live="polite"
        >
            <div className="vss-toast__icon">
                {icon[type]}
            </div>

            <div className="vss-toast__content">
                {title && <p className="vss-toast__title">{title}</p>}

                <p className="vss-toast__message">{message}</p>
            </div>

            {onClose && (
                <button
                    type="button"
                    className="vss-toast__close"
                    aria-label="Close notification"
                    onClick={onClose}
                >
                    <CrossIcon/>
                </button>
            )}
        </div>
    );
});
